import React, { useEffect, useRef } from 'react';
import '../styles/carousel.css';

const Carousel = ({ images }) => {
    // use double images to achieve seamless loop
    const doubleImages = [...images, ...images];
    const carouselRef = useRef(null);

    useEffect(() => {
        const carousel = carouselRef.current;
        if (!carousel) return;

        // image width + gap
        const imageWidth = 400;
        const gap = 24;
        const itemWidth = imageWidth + gap;

        // total move distance: exactly the width of the original images
        const totalMoveDistance = images.length * itemWidth;

        // create dynamic CSS animation
        const animationName = `carousel-move-${images.length}`;

        // check if the animation already exists
        const existingStyle = document.getElementById(animationName);
        if (existingStyle) {
            existingStyle.remove();
        }

        const styleElement = document.createElement('style');
        styleElement.id = animationName;
        styleElement.textContent = `
            @keyframes ${animationName} {
                0% {
                    transform: translateX(0px);
                }
                100% {
                    transform: translateX(-${totalMoveDistance}px);
                }
            }
        `;
        document.head.appendChild(styleElement);

        // calculate animation time: each image shows for 3 seconds (faster speed)
        const animationDuration = images.length * 3;

        // apply animation
        carousel.style.animation = `${animationName} ${animationDuration}s linear infinite`;

        // handle time synchronization after page refresh
        const savedTime = localStorage.getItem('carousel-start-time');
        const now = Date.now();

        if (savedTime) {
            const elapsedSeconds = (now - parseInt(savedTime)) / 1000;
            const animationDelay = -(elapsedSeconds % animationDuration);
            carousel.style.animationDelay = `${animationDelay}s`;
        } else {
            localStorage.setItem('carousel-start-time', now.toString());
        }

        // handle page visibility change
        const handleVisibilityChange = () => {
            if (document.hidden) {
                carousel.style.animationPlayState = 'paused';
            } else {
                carousel.style.animationPlayState = 'running';
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            // clean up dynamic created styles
            const styleToRemove = document.getElementById(animationName);
            if (styleToRemove) {
                styleToRemove.remove();
            }
        };
    }, [images.length]);

    // handle mouse hover
    const handleMouseEnter = () => {
        if (carouselRef.current) {
            carouselRef.current.style.animationPlayState = 'paused';
        }
    };

    const handleMouseLeave = () => {
        if (carouselRef.current) {
            carouselRef.current.style.animationPlayState = 'running';
        }
    };

    return (
        <div
            className="w-full max-w-[1600px] mx-auto relative overflow-hidden px-4"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div
                ref={carouselRef}
                className="flex gap-6"
            >
                {doubleImages.map((image, index) => (
                    <div
                        key={`${image.alt}-${index}`}
                        className="flex-shrink-0 w-[400px]"
                    >
                        <div className="relative aspect-[4/3] w-full h-[533px]">
                            <img
                                src={image.src}
                                alt={`${image.alt} ${index >= images.length ? '(copy)' : ''}`}
                                className="w-full h-full object-cover rounded-lg"
                                draggable="false"
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Carousel; 